import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const ProductEditor = ({ brandId, brandSlug }) => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [formData, setFormData] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploading, setUploading] = useState(false);

  const getAuthHeaders = (isJson = true) => {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (isJson) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  };

  // Fetch products on mount
  useEffect(() => {
    fetchProducts();
    fetchAvailableModels();
  }, [brandId]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/storefront/products?brandId=${brandId}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      setProducts(data.products || []);

      if (data.products && data.products.length > 0 && !selectedProductId) {
        setSelectedProductId(data.products[0].id);
        setFormData(data.products[0]);
      }
    } catch (error) {
      toast.error('Failed to load products');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableModels = async () => {
    try {
      const response = await fetch(`/api/storefront/models?brandId=${brandId}`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      setAvailableModels(data.models || []);
    } catch (error) {
      console.error('Failed to load models:', error);
    }
  };

  // Select product
  useEffect(() => {
    if (selectedProductId && products.length > 0) {
      const product = products.find(p => p.id === selectedProductId);
      if (product) {
        setFormData({ ...product });
        setHasChanges(false);
      }
    }
  }, [selectedProductId, products]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleDetailChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      details: { ...prev.details, [field]: value }
    }));
    setHasChanges(true);
  };

  const handleTierChange = (index, field, value) => {
    const updatedTiers = [...formData.tiers];
    updatedTiers[index] = { ...updatedTiers[index], [field]: value };
    setFormData(prev => ({ ...prev, tiers: updatedTiers }));
    setHasChanges(true);
  };

  const handleAddTier = () => {
    const newTier = {
      id: `t${formData.tiers.length + 1}`,
      name: 'New Tier',
      price: formData.basePrice,
      multiplier: 1.0,
      window: '8-10 weeks',
      target: 30,
      current: 0
    };
    setFormData(prev => ({ ...prev, tiers: [...prev.tiers, newTier] }));
    setHasChanges(true);
  };

  const handleRemoveTier = (index) => {
    const updatedTiers = formData.tiers.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, tiers: updatedTiers }));
    setHasChanges(true);
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      const uploadedUrls = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('brandId', brandId);

        const response = await fetch('/api/storefront/images/upload', {
          method: 'POST',
          headers: getAuthHeaders(false),
          body: uploadFormData,
        });

        if (response.ok) {
          const data = await response.json();
          uploadedUrls.push(data.url);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      if (uploadedUrls.length > 0) {
        setFormData(prev => ({
          ...prev,
          images: [...(prev.images || []), ...uploadedUrls]
        }));
        setHasChanges(true);
        toast.success(`Uploaded ${uploadedUrls.length} image(s)`);
      }
    } catch (error) {
      toast.error('Error uploading images');
      console.error(error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      const response = await fetch('/api/storefront/products/save', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          brandId,
          productId: selectedProductId,
          product: formData
        }),
      });

      if (response.ok) {
        toast.success('Product saved successfully');
        setHasChanges(false);
        fetchProducts(); // Refresh list
      } else {
        toast.error('Failed to save product');
      }
    } catch (error) {
      toast.error('Error saving product');
      console.error(error);
    }
  };

  const handleCreateProduct = async () => {
    try {
      const newProduct = {
        id: `product_${Date.now()}`,
        name: 'New Product',
        basePrice: 100,
        description: '',
        modelUrl: '',
        images: [],
        details: {},
        tiers: [],
        currentViewers: 0,
        recentReservations: 0
      };

      const response = await fetch('/api/storefront/products/create', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          brandId,
          product: newProduct
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Product created');
        fetchProducts();
        setSelectedProductId(data.productId);
      } else {
        toast.error('Failed to create product');
      }
    } catch (error) {
      toast.error('Error creating product');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading products...</Typography>
      </Box>
    );
  }

  if (!formData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          No products yet. Create your first product to get started.
        </Alert>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateProduct}
        >
          Create First Product
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Select Product</InputLabel>
          <Select
            value={selectedProductId || ''}
            onChange={(e) => setSelectedProductId(e.target.value)}
            label="Select Product"
          >
            {products.map(product => (
              <MenuItem key={product.id} value={product.id}>
                {product.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateProduct}
          >
            New Product
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges}
          >
            {hasChanges ? 'Save Changes' : 'Saved'}
          </Button>
        </Box>
      </Box>

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have unsaved changes
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Product Name"
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Base Price"
                    type="number"
                    value={formData.basePrice || 0}
                    onChange={(e) => handleChange('basePrice', parseFloat(e.target.value))}
                    InputProps={{ startAdornment: '$' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={4}
                    value={formData.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* 3D Model */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                3D Model
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select Model</InputLabel>
                <Select
                  value={formData.modelUrl || ''}
                  onChange={(e) => handleChange('modelUrl', e.target.value)}
                  label="Select Model"
                >
                  <MenuItem value="">No model</MenuItem>
                  {availableModels.map((model) => (
                    <MenuItem key={model} value={model}>
                      {model.replace('/models/', '')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.modelUrl && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Current: {formData.modelUrl}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Social Proof */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Social Proof
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Current Viewers"
                    type="number"
                    value={formData.currentViewers || 0}
                    onChange={(e) => handleChange('currentViewers', parseInt(e.target.value))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Recent Reservations"
                    type="number"
                    value={formData.recentReservations || 0}
                    onChange={(e) => handleChange('recentReservations', parseInt(e.target.value))}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Product Images */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Product Images ({formData.images?.length || 0})
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<UploadIcon />}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Images'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploading}
                  />
                </Button>
              </Box>

              {formData.images && formData.images.length > 0 ? (
                <Grid container spacing={2}>
                  {formData.images.map((imageUrl, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box sx={{ position: 'relative', paddingTop: '100%', bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                        <img
                          src={imageUrl}
                          alt={`Product ${index + 1}`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <IconButton
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' }
                          }}
                          size="small"
                          onClick={() => handleRemoveImage(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">
                  No images added. Click "Upload Images" to add product images.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pricing Tiers */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Pricing Tiers ({formData.tiers?.length || 0})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddTier}
                >
                  Add Tier
                </Button>
              </Box>

              <Grid container spacing={2}>
                {formData.tiers?.map((tier, index) => (
                  <Grid item xs={12} key={tier.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            Tier {index + 1}
                          </Typography>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleRemoveTier(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              label="Name"
                              size="small"
                              value={tier.name}
                              onChange={(e) => handleTierChange(index, 'name', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              label="Price"
                              type="number"
                              size="small"
                              value={tier.price}
                              onChange={(e) => handleTierChange(index, 'price', parseFloat(e.target.value))}
                              InputProps={{ startAdornment: '$' }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              label="Multiplier"
                              type="number"
                              size="small"
                              inputProps={{ step: 0.1 }}
                              value={tier.multiplier}
                              onChange={(e) => handleTierChange(index, 'multiplier', parseFloat(e.target.value))}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              label="Delivery Window"
                              size="small"
                              value={tier.window}
                              onChange={(e) => handleTierChange(index, 'window', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              label="Target Spots"
                              type="number"
                              size="small"
                              value={tier.target}
                              onChange={(e) => handleTierChange(index, 'target', parseInt(e.target.value))}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              label="Current Reservations"
                              type="number"
                              size="small"
                              value={tier.current}
                              onChange={(e) => handleTierChange(index, 'current', parseInt(e.target.value))}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={6}>
                            <TextField
                              fullWidth
                              label="Deadline"
                              type="datetime-local"
                              size="small"
                              value={tier.deadline ? new Date(tier.deadline).toISOString().slice(0, 16) : ''}
                              onChange={(e) => handleTierChange(index, 'deadline', e.target.value ? new Date(e.target.value).toISOString() : '')}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductEditor;
